/**
 * EducationInstitutionDetail — /education/institutions/:slug
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  GraduationCap, MapPin, Phone, Mail, Globe, Loader2, Video,
  BookOpen, DollarSign, ClipboardList, Building2, Sparkles,
} from "lucide-react";
import { http } from "@/lib/api";
import EducationCategoryNav from "@/components/EducationCategoryNav";
import VideoEmbed from "@/components/VideoEmbed";

export default function EducationInstitutionDetail() {
  const { slug } = useParams();
  const [i, setI] = useState(null);
  const [err, setErr] = useState(null);
  const [meta, setMeta] = useState(null);

  useEffect(() => { http.get("/education/meta").then(r => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    http.get(`/education/institutions/${slug}`).then(r => setI(r.data)).catch(e => setErr(e?.response?.data?.detail || "Not found"));
  }, [slug]);

  if (err) return (
    <><EducationCategoryNav />
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <h1 className="font-display text-2xl mb-2">Institution not found</h1>
        <p className="opacity-70">{err}</p>
        <Link to="/education/institutions" className="wofy-link inline-block mt-4">← Back</Link>
      </div>
    </>
  );
  if (!i) return <><EducationCategoryNav /><div className="max-w-3xl mx-auto px-4 py-12 opacity-60 inline-flex gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div></>;

  const typeLabel = meta?.institution_types?.find(t => t.slug === i.institution_type)?.label || i.institution_type;
  const levelLabel = meta?.institution_levels?.find(l => l.slug === i.level)?.label;
  const categoryLabel = meta?.institution_categories?.find(c => c.slug === i.category)?.label;

  return (
    <>
      <EducationCategoryNav />
      <article className="max-w-4xl mx-auto px-4 py-8" data-testid="institution-detail">
        {i.hero_image_url && (
          <div className="w-full h-56 sm:h-72 rounded-xl overflow-hidden mb-6 bg-black/5"
               style={{ backgroundImage: `url(${i.hero_image_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        )}

        <div className="flex items-start gap-4 mb-4">
          {i.logo_url ? (
            <img src={i.logo_url} alt="" className="w-16 h-16 rounded-full object-cover ring-2 ring-white" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-black/5 grid place-items-center text-xl font-mono">
              {(i.name || "?")[0].toUpperCase()}
            </div>
          )}
          <div>
            <div className="overline mb-1">
              <Building2 className="w-3 h-3 inline mr-1" />{typeLabel}
              {levelLabel && <span className="opacity-70"> · {levelLabel}</span>}
              {categoryLabel && <span className="opacity-70"> · {categoryLabel}</span>}
            </div>
            <h1 className="font-display text-4xl leading-tight">{i.name}</h1>
            {(i.city || i.country) && (
              <div className="text-sm opacity-70 flex items-center gap-1 mt-1">
                <MapPin className="w-3.5 h-3.5" />{[i.city, i.state, i.country].filter(Boolean).join(", ")}
                {i.founded_year && <span className="ml-2 opacity-60">· Est. {i.founded_year}</span>}
              </div>
            )}
            {i.accreditation && <div className="text-xs opacity-60 mt-1">Accredited by {i.accreditation}</div>}
          </div>
        </div>

        {i.description && <p className="whitespace-pre-line leading-relaxed opacity-90 mb-6">{i.description}</p>}

        {/* Instant website CTA */}
        <div className="wofy-card !p-3 mb-6 flex items-center gap-3 flex-wrap"
             style={{ background: "var(--wofy-surface)" }} data-testid="website-cta">
          <Sparkles className="w-5 h-5 opacity-70" />
          <div className="text-sm flex-1 min-w-0">
            <b>Instant Website for this institution</b>
            <div className="text-xs opacity-70">Auto-fill from this profile, pick a template, publish in minutes.</div>
          </div>
          <Link to={`/websites/new?source_type=institution&source_id=${i.id}`}
            className="wofy-btn-primary text-xs inline-flex items-center gap-1"
            data-testid="cta-instant-website">
            <Sparkles className="w-3.5 h-3.5" />Get a free website
          </Link>
        </div>

        {i.courses_offered?.length > 0 && (
          <section className="mb-8">
            <h2 className="font-display text-2xl mb-2 inline-flex items-center gap-2">
              <BookOpen className="w-5 h-5" />Courses offered
            </h2>
            <div className="flex flex-wrap gap-2">
              {i.courses_offered.map((c, k) => (
                <span key={k} className="text-xs px-2.5 py-1 rounded-full border" style={{ borderColor: "var(--wofy-border)" }}>{c}</span>
              ))}
            </div>
          </section>
        )}

        {i.admission_summary && (
          <section className="mb-8">
            <h2 className="font-display text-2xl mb-2 inline-flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />Admissions
            </h2>
            <p className="whitespace-pre-line text-sm opacity-90 leading-relaxed">{i.admission_summary}</p>
          </section>
        )}

        {i.fees_summary && (
          <section className="mb-8">
            <h2 className="font-display text-2xl mb-2 inline-flex items-center gap-2">
              <DollarSign className="w-5 h-5" />Fees
            </h2>
            <p className="whitespace-pre-line text-sm opacity-90 leading-relaxed">{i.fees_summary}</p>
          </section>
        )}

        {i.video_links?.length > 0 && (
          <section className="mb-8" data-testid="institution-videos">
            <h2 className="font-display text-2xl mb-3 inline-flex items-center gap-2">
              <Video className="w-5 h-5" />Videos
            </h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {i.video_links.map((v, k) => <VideoEmbed key={k} video={v} />)}
            </div>
          </section>
        )}

        {i.photo_urls?.length > 0 && (
          <section className="mb-8">
            <h2 className="font-display text-2xl mb-3">Gallery</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {i.photo_urls.map((p, k) => <img key={k} src={p} alt="" className="w-full aspect-square object-cover rounded-lg" />)}
            </div>
          </section>
        )}

        {(i.contact_email || i.contact_phone || i.website || i.address) && (
          <section className="wofy-card !p-4">
            <div className="overline mb-1">CONTACT</div>
            {i.address && <div className="text-sm opacity-90 mb-1">{i.address}</div>}
            <div className="text-sm opacity-80 flex flex-wrap gap-3">
              {i.contact_email && <a href={`mailto:${i.contact_email}`} className="inline-flex items-center gap-1 wofy-link"><Mail className="w-3.5 h-3.5" />{i.contact_email}</a>}
              {i.contact_phone && <a href={`tel:${i.contact_phone}`} className="inline-flex items-center gap-1 wofy-link"><Phone className="w-3.5 h-3.5" />{i.contact_phone}</a>}
              {i.website && <a href={i.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 wofy-link"><Globe className="w-3.5 h-3.5" />Website</a>}
            </div>
          </section>
        )}
      </article>
    </>
  );
}
